<template>
  <div>
    <div class="payout-bulk-generation">
      <div class="row">
        <div class="col col-12 col-md-2">
          <label class="form__label">Date From:</label>
          <Datepicker
            :bootstrap-styling="true"
            format="yyyy-MM-dd"
            :value="_toIsoStringTime(dateFrom)"
            @input="dateFrom = _toLocalTime($event, defaultDateFormat)"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">Date To:</label>
          <Datepicker
            :bootstrap-styling="true"
            format="yyyy-MM-dd"
            :value="_toIsoStringTime(dateTo)"
            @input="dateTo = _toLocalTime($event, defaultDateFormat)"
          />
        </div>
        <div class="col col-12 col-md-3 align-self-end">
          <b-button
            type="submit"
            class="button ml-2 float-left"
            variant="primary"
            @click.prevent="showCreationModal = true">
            Generate invoices
          </b-button>
        </div>
      </div>
    </div>
    <b-modal
      size="lg"
      hide-footer
      v-model="showCreationModal"
      class="modal"
      @hide="hideCreationModal"
    >
      <template v-if="isDataLoading === false">
        <h6 v-b-toggle.collapse-gen-by-worklogs>Generation based on non-covered Jira worklogs</h6>
        <b-collapse visible id="collapse-gen-by-worklogs" class="mt-2">
          <b-card>
            <div
              class="row mb-2"
              v-for="(message, index) in messages"
              :key="index"
            >
                <div class="col col-12 col-md-8">
                  <div v-html="message.text"></div>
                  <div v-if="message.previous" class="ml-3">
                    Previous:&nbsp;
                    <InvoicesLinks
                      :templates="getTemplates"
                      :item="message.previous"
                      @savedInvoice="savedInvoiceManually(message, 'previous', $event)"
                    />
                  </div>
                  <div
                    v-if="message.errors && message.errors.length"
                    class="small pl-2 status-error">{{ message.errors.join(' | ') }}
                  </div>
                </div>
                <div class="col col-12 col-md-2 message-status">
                  <span v-if="message.loading" class="small-spinner spinner-border text-secondary" role="status">
                    <span class="sr-only">Creating...</span>
                  </span>
                  <div
                    v-else-if="message.status === 'success'"
                    class="status-success status-icon"
                    style="float:left"
                    v-b-tooltip.html title="Success"
                  >&check;</div>
                  <span
                    v-else-if="message.status === 'error'"
                    class="status-error status-icon"
                    v-b-tooltip.html title="Error"
                  >&#9888;</span>
                  <span
                    v-else-if="message.status === 'warning'"
                    class="status-warning status-icon"
                    v-b-tooltip.html title="Warning"
                  >&#9888;</span>
                  <template v-if="message.item">
                    <span
                      v-if="message.item.extra_payments && message.item.extra_payments.length"
                      class="ep-message"
                      v-b-tooltip.html :title="'This invoice has extra payments copied from last payee invoice'"
                    >EP <font-awesome-icon icon="info" style="font-size: 0.75rem;"/></span>
                    <span
                      v-if="message.item.hasNotMonthPeriods"
                      class="ep-message"
                      v-b-tooltip.html :title="'In the previous invoice, some items have a non-full-month interval - please make sure the interval in the new invoice is correct'"
                    >PR <font-awesome-icon icon="info" style="font-size: 0.75rem;"/></span>
                  </template>
                </div>
                <div class="col col-12 col-md-2">
                  <InvoicesLinks
                    v-if="message.item"
                    :templates="getTemplates"
                    :item="message.item"
                    @savedInvoice="savedInvoiceManually(message, 'item', $event)"
                  />
                </div>
            </div>
            <div class="d-flex justify-content-center mt-2">
                <button
                  class="btn button btn-primary"
                  v-if="isProcessedCreation && invoicesForCreationCount > 0"
                  @click="isProcessedCreation = false">Stop creation ({{ invoicesForCreationCount }} left)</button>
                <button
                  v-else-if="invoicesForCreationCount > 0"
                  class="btn button btn-primary"
                  @click="processCreation()">Create {{ invoicesForCreationCount }} invoices</button>
            </div>
          </b-card>
        </b-collapse>
        <br />
        <h6 v-b-toggle.collapse-copy-extra-paymented>Invoices of types “Only extra payments” or “By project” (created in last 2 months or has worklogs in current month)</h6>
        <b-collapse visible id="collapse-copy-extra-paymented" class="mt-2">
          <b-card>
            <template v-if="copyInvoices.length">
              <div
                class="row mb-2"
                v-for="({ original, item, status }, index) in copyInvoices"
                :key="index"
              >
                <div class="col col-12 col-md-6">
                  {{ original.project.name }} {{ _toLocalTime(original.issue_date, defaultDateFormat) }}
                  <div style="float:right">
                    Previous:
                    <InvoicesLinks
                      v-if="original"
                      :item="original"
                      :templates="getTemplates"
                      @savedInvoice="savedInvoiceManually(copyInvoices[index], 'original', $event)"
                    />
                  </div>
                </div>
                <div class="message-status text-center" style="width: 99px">
                  <div v-if="status === 'new'"
                       class="button btn btn-success"
                       @click.prevent="copyInvoice(index)">
                    <font-awesome-icon icon="copy" style="margin:2px 7px 0px 0px"/> Copy &#8680;</div>
                  <div v-else-if="status === 'creating'" class="spinner-border text-primary">
                      <span class="sr-only">Creating...</span>
                  </div>
                  <div v-else-if="status === 'created'">
                    <div class="status-success status-icon" v-b-tooltip.html title="Created">&check;</div>
                  </div>
                </div>
                <div class="col">
                  <template v-if="item">
                    {{ item.project.name }} {{ _toLocalTime(item.issue_date, defaultDateFormat) }}
                    <InvoicesLinks
                      style="float:right;"
                      v-if="item"
                      :item="item"
                      :templates="getTemplates"
                      @savedInvoice="savedInvoiceManually(copyInvoices[index], 'item', $event)"
                    />
                  </template>
                  <template v-else>
                    No Invoice for {{ original.project.name }} for {{ Moment().format('MMM YYYY') }}
                  </template>
                </div>
              </div>
            </template>
            <template v-else>
              No Invoices
            </template>
          </b-card>
        </b-collapse>
      </template>
      <template v-else>
        <div class="loading-message">
          <h5>Loading, please wait</h5>
        </div>
        <Preloader />
      </template>
    </b-modal>

    <FinanceInvoicesModal
      ref="invoiceModalAuto"
      mode="create"
      :value="false"
      :bankDetails="getOwnBankDetails"
      :clientBankDetails="getClientsBankDetailsAll"
      :templates="getTemplates"
      :projects="getActualProjects"
      :statuses="getStatuses"
      :types="getInvoicesTypes"
    />
    <FinanceInvoicesModal
      ref="invoiceModalAutoCopy"
      mode="create"
      :value="false"
      :bankDetails="getOwnBankDetails"
      :clientBankDetails="getClientsBankDetailsAll"
      :templates="getTemplates"
      :projects="getActualProjects"
      :statuses="getStatuses"
      :types="getInvoicesTypes"
    />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import Moment from 'moment'
import Datepicker from 'vuejs-datepicker'
import { mapActions, mapGetters } from 'vuex'
import { permissions } from '@mocks/permissions'
import Preloader from '@components/Preloader'
import FinanceInvoicesModal from '@components/modals/FinanceInvoicesModal'
import InvoicesLinks from '../components/InvoicesLinks'
import InvoiceFormModal from '../mixins/InvoiceFormModal'

export default {
  name: 'InvoicesBulkCreation',
  mixins: [ InvoiceFormModal ],
  components: {
    Datepicker,
    Preloader,
    FinanceInvoicesModal,
    InvoicesLinks
  },
  props: {
    templates: {
      type: Array
    }
  },
  data () {
    const defaultDateFormat = 'YYYY-MM-DD'
    return {
      permissions,
      defaultDateFormat,
      showCreationModal: false,
      isDataPageLoading: false,
      isProcessedCreation: false,
      openedInvoiceMessage: null,
      dateFrom: Moment().subtract(1, 'months').startOf('month').format(defaultDateFormat),
      dateTo: Moment().subtract(1, 'months').endOf('month').format(defaultDateFormat),
      isDataLoading: false,
      dataForCreation: [],
      copyInvoices: [],
      messages: []
    }
  },
  computed: {
    ...mapGetters({
      getOwnBankDetails: 'finances/getOwnBankDetails',
      getStatuses: 'finances/getStatuses',
      getProjects: 'projects/getProjects',
      getTemplates: 'finances/getTemplates',
      getClientsBankDetailsAll: 'finances/getClientsBankDetailsAll',
      getInvoicesTypes: 'finances/getInvoicesTypes',
      getIsModalLoading: 'finances/getIsModalLoading'
    }),
    getActualProjects () {
      return this.getProjects
        .filter(project => project.project_status.name !== 'done')
    },
    invoicesForCreationCount () {
      return this.dataForCreation.filter(item => !item.processed).length
    },
    currentProcessedInvoiceNumber () {
      return this.dataForCreation.filter(item => item.processed).length + 1
    }
  },
  watch: {
    'showCreationModal': function (val) {
      if (val) {
        this.generateInvoices()
      }
    }
  },
  methods: {
    Moment: function (date) {
      return Moment(date)
    },
    ...mapActions({
      setIsModalLoading: 'finances/setIsModalLoading'
    }),
    async generateInvoices () {
      this.showCreationModal = true

      this.dataForCreation = []
      this.copyInvoices = []
      this.messages = []

      this.isDataLoading = true
      const dataForCreationResponse = await this.fetchDataForCreation()
      this.isDataLoading = false

      this.dataForCreation = Object.values(dataForCreationResponse.data.availablePeriods).map(item => {
        item.processed = false
        return item
      })
      this.copyInvoices = Object.values(dataForCreationResponse.data.copyInvoices).map(item => {
        return {
          original: item,
          item: null,
          status: 'new'
        }
      })

      this.messages.push({
        type: 'info',
        text: 'Fetched <b>' + this.dataForCreation.length + ' items</b>',
        status: 'success'
      })
    },
    async hideCreationModal (event) {
      if (event.trigger !== 'confirmed') {
        event.preventDefault()
      }
      const confirm = await this.$root.$confirm.open(
        'Close Bulk Creation',
        'Are you sure you want to stop bulk creation process and close this window?'
      )
      if (confirm) {
        event.vueTarget.hide('confirmed')
        this.showCreationModal = false
        if (this.messages.filter(item => item.type === 'creation' && item.status === 'success').length > 0) {
          this.$emit('updateInvoices')
        }
      }
    },
    saveMessageSuccess (message, item) {
      const itemAttrName = message.itemAttrName ?? 'item'
      if (itemAttrName === 'item') {
        message.status = 'success'
      }
      message[itemAttrName] = item ? Object.assign(message[itemAttrName], item) : message[itemAttrName]
      message.errors = []
      message.loading = false
    },
    saveMessageError (message, errors) {
      message.status = 'error'
      message.errors = message.errors ? message.errors.push(errors) : errors
      message.loading = false
    },
    async processCreation () {
      this.isProcessedCreation = true

      for (const index in this.dataForCreation) {
        const item = this.dataForCreation[index]
        if (!this.isProcessedCreation) {
          break
        }
        if (item.processed) {
          continue
        }

        const dateFrom = this.dateFrom
        const dateTo = this.dateTo
        const projectName = item.project.name

        this.messages.push({
          type: 'creation',
          status: 'in-progress',
          text: this.currentProcessedInvoiceNumber + '. Creating  <b>' + projectName + '</b> invoice for: ' + this._toLocalTime(dateFrom, this.defaultDateFormat) + ' - ' + this._toLocalTime(dateTo, this.defaultDateFormat),
          loading: true
        })

        item.processed = true
        this.dataForCreation[index] = item

        let processedInvoiceMessage = this.messages.at(-1)

        let invoiceData = {}
        this.$refs['invoiceModalAuto'].resetForm()

        this.$refs['invoiceModalAuto'].form.fields.project.value = cloneDeep(item.project)
        this.$refs['invoiceModalAuto'].form.fields.invoice_status.value = this.getInvoiceStatus('new')
        await this.$refs['invoiceModalAuto'].replaceCommentsWithProjectName()
        this.$refs['invoiceModalAuto'].form.fields.auto_fill.value = cloneDeep({ date_from: dateFrom, date_to: dateTo })

        const formFields = cloneDeep(this.$refs['invoiceModalAuto'].form.fields)
        Object.keys(formFields).forEach(function (key) {
          invoiceData[key] = formFields[key].value
        })

        const projectId = item.project.id
        let errors = []
        const clientBankDetailsData = await this.$refs['invoiceModalAuto'].fetchClientBanks(projectId)

        if (!clientBankDetailsData || clientBankDetailsData.length === 0) {
          errors.push('Project has no client bank details')
          processedInvoiceMessage.item = cloneDeep(invoiceData)
          this.saveMessageError(processedInvoiceMessage, errors)
          continue
        }

        const { data: lastProjectInvoiceData } = await this.$refs['invoiceModalAuto'].fetchPreviousProjectInvoice(projectId)

        if (Object.keys(lastProjectInvoiceData).length) {
          invoiceData['extra_payments'] = lastProjectInvoiceData.extra_payments
          invoiceData['client_bank_details'] = lastProjectInvoiceData.client_bank_details
          invoiceData['own_bank_details'] = lastProjectInvoiceData.own_bank_details
          if (lastProjectInvoiceData.invoice_template_type.name !== 'custom') {
            invoiceData['invoice_template_type'] = lastProjectInvoiceData.invoice_template_type
          }
          this.$refs['invoiceModalAuto'].previousInvoiceData.items = lastProjectInvoiceData.items
          this.$refs['invoiceModalAuto'].previousInvoiceData.periods = lastProjectInvoiceData.periods

          const checkNotMonthPeriods =
            item => this.Moment(item.date_from).format(this.defaultDateFormat) !== this.Moment(item.date_from).startOf('month').format(this.defaultDateFormat) ||
                    this.Moment(item.date_to).format(this.defaultDateFormat) !== this.Moment(item.date_to).endOf('month').format(this.defaultDateFormat)

          const lastProjectInvoiceType = lastProjectInvoiceData.invoice_type

          if (this.$refs['invoiceModalAuto'].isEmployeeTm(lastProjectInvoiceType.id)) {
            if (lastProjectInvoiceData.items.some(checkNotMonthPeriods)) {
              invoiceData.hasNotMonthPeriods = true
              processedInvoiceMessage.previous = lastProjectInvoiceData
            }
            invoiceData['invoice_type'] = lastProjectInvoiceType
            invoiceData['items'] = lastProjectInvoiceData.items
          }

          if (this.$refs['invoiceModalAuto'].isEmployeePeriod(lastProjectInvoiceType.id)) {
            if (lastProjectInvoiceData.periods.some(checkNotMonthPeriods)) {
              invoiceData.hasNotMonthPeriods = true
              processedInvoiceMessage.previous = lastProjectInvoiceData
            }
            invoiceData['invoice_type'] = lastProjectInvoiceType
            invoiceData['periods'] = lastProjectInvoiceData.periods
          }

          invoiceData['sales_commission_percent'] = lastProjectInvoiceData.sales_commission_percent
          invoiceData['commission'] = lastProjectInvoiceData.commission
        } else {
          invoiceData['client_bank_details'] = clientBankDetailsData[0]
          const currencyCode = invoiceData['client_bank_details'].currency.code
          invoiceData['own_bank_details'] = this.$refs['invoiceModalAuto'].form.fields.own_bank_details.options.find(item => item.currency.code === currencyCode)
        }

        if (!invoiceData.client_bank_details) {
          errors.push('Client bank details is required')
        }
        if (!invoiceData.own_bank_details) {
          errors.push('Own bank details is required')
        }

        if (errors.length > 0) {
          processedInvoiceMessage.item = cloneDeep(invoiceData)
          this.saveMessageError(processedInvoiceMessage, errors)
          continue
        }

        await this.$refs['invoiceModalAuto'].setParamsForEditFinanceInvoicesModal(invoiceData)

        const autoFillItems = await this.$refs['invoiceModalAuto'].autoFillRequest({
          project_id: projectId, invoice_type_id: invoiceData['invoice_type']['id'], date_from: dateFrom, date_to: dateTo
        })

        if (!autoFillItems) {
          errors.push('No items for project available')
        } else {
          if (this.$refs['invoiceModalAuto'].isEmployeeTm()) {
            invoiceData.items = cloneDeep(this.$refs['invoiceModalAuto'].form.fields.items.value)
            if (invoiceData.items.some(item => item.hour_cost === 0)) {
              errors.push('Need set hours cost for items')
            }
          } else if (this.$refs['invoiceModalAuto'].isEmployeePeriod()) {
            invoiceData.periods = cloneDeep(this.$refs['invoiceModalAuto'].form.fields.periods.value)
            if (invoiceData.items.some(item => item.period_cost === 0)) {
              errors.push('Need set period cost for items')
            }
          }
        }

        processedInvoiceMessage.item = cloneDeep(invoiceData)

        if (errors.length > 0) {
          this.saveMessageError(processedInvoiceMessage, errors)
          continue
        }

        await this.$refs['invoiceModalAuto'].setParamsForEditFinanceInvoicesModal(invoiceData)
        const invoiceDataRequest = this.prepareDataForEditAndCreateRequest(this.$refs['invoiceModalAuto'].form.fields)
        await this.saveInvoiceBulk(invoiceDataRequest, processedInvoiceMessage)
      }
    },
    async saveInvoiceBulk (invoiceDataForRequest, invoiceMessage) {
      const that = this
      return this.saveInvoice(invoiceDataForRequest, invoiceMessage, async function () {
        if (invoiceDataForRequest.id) {
          return that.$api.finances.updateInvoices(invoiceDataForRequest)
        } else {
          return that.$api.finances.createInvoices(invoiceDataForRequest)
        }
      })
    },
    savedInvoiceManually (message, attrName, { status, data }) {
      message.itemAttrName = attrName
      if (status === 200) {
        this.saveMessageSuccess(message, data)
      } else {
        const errors = ['Error payout saving']
        this.saveMessageError(message, errors)
      }
    },
    async saveInvoice (invoiceDataForRequest, invoiceMessage, saveInvoiceFunc) {
      try {
        let invoiceResponse
        invoiceResponse = await saveInvoiceFunc()
        const invoiceData = invoiceResponse.data
        if (invoiceResponse.status === 200) {
          this.saveMessageSuccess(invoiceMessage, invoiceData)
        }
      } catch (e) {
        console.log(e)
        const errors = []
        if (e.response.data.errors) {
          errors.push(Object.values(e.response.data.errors).map(itemErrors => itemErrors[0]))
        } else {
          errors.push(e.message)
        }

        this.saveMessageError(invoiceMessage, errors)
      }
    },
    fetchDataForCreation () {
      return this.axios
        .post('/api/finances/invoices/data-for-bulk-creation',
          {
            date_from: this.dateFrom,
            date_to: this.dateTo
          },
          { headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') } })
        .catch((error) => {
          this.errorNotifications(error)
        })
    },
    async copyInvoice (index) {
      const original = this.copyInvoices[index].original
      const item = cloneDeep(original)
      this.copyInvoices[index].status = 'creating'

      item.invoice_status = this.getInvoiceStatus('new')
      await this.$refs['invoiceModalAutoCopy'].setParamsForEditFinanceInvoicesModal(item)
      const invoiceDataRequest = this.prepareDataForEditAndCreateRequest(this.$refs['invoiceModalAutoCopy'].form.fields)

      invoiceDataRequest.items = []
      invoiceDataRequest.periods = []
      invoiceDataRequest.issue_date = this.Moment().format(this.defaultDateFormat)
      invoiceDataRequest.due_date = this.Moment().add(3, 'days').format(this.defaultDateFormat)
      invoiceDataRequest.paid_date = null

      const invoiceResponse = await this._toggleDataPageLoading(async () => {
        return this.$api.finances.createInvoices(invoiceDataRequest)
      })
      if (invoiceResponse.data) {
        this.copyInvoices[index].item = invoiceResponse.data
        this.copyInvoices[index].status = 'created'
      } else {
        this.copyInvoices[index].item = item
        this.copyInvoices[index].status = 'error'
      }
    },
    getInvoiceStatus (name) {
      return this.$refs['invoiceModalAuto'].statuses.find(el => el.name === name)
    }
  }
}
</script>

<style lang="scss">
  .status-icon {
    font-size: x-large;
  }
  .status-error {
    color: red;
  }
  .status-success {
    color: #3fa52f;
  }
  .status-warning {
    color: red;
  }
  .small-spinner.spinner-border {
    width: 1rem;
    height: 1rem;
  }
  .message-item {
    display: flex;
  }
  .message-item-right-box {
    margin-left: auto;
  }
  .loading-message {
    text-align:center;
  }
  .ep-message {
    margin-left: 10px;
  }
</style>
